import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { INLINE_BLOCK_EDITOR_NAME, useEditorsMap } from '@providers/editors-map';

import { ACCEPTED_FILE_EXTENSIONS } from '../constants';
import { useClauseFileImport } from '../hooks/useClauseFileImport';
import type { ClauseFileImportOutcome } from '../types';
import { FileDropZone } from '../views/FileDropZone';
import { ImportFileToolbarButton } from '../views/ImportFileToolbarButton';
import { showImportRejectedToast } from '../views/ImportToasts';

interface ClauseFileImportControlProps {
  outcome: ClauseFileImportOutcome;
}

export const ClauseFileImportControl = ({ outcome }: ClauseFileImportControlProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isZoneOpen, setIsZoneOpen] = useState(false);
  const { importFile, isConverting } = useClauseFileImport(outcome);
  const { getEditor } = useEditorsMap();

  const editor = getEditor(INLINE_BLOCK_EDITOR_NAME);
  const blockElement = editor?.view.dom.closest<HTMLElement>('form') ?? null;

  const handleFiles = (files: FileList) => {
    if (isConverting) {
      return;
    }

    if (files.length > 1) {
      showImportRejectedToast('multipleFiles');
      return;
    }

    const file = files[0];
    if (file) {
      importFile(file, () => setIsZoneOpen(false));
    }
  };

  useEffect(() => {
    if (!editor || !isConverting) {
      return;
    }

    editor.setEditable(false);
    return () => editor.setEditable(true);
  }, [editor, isConverting]);

  useEffect(() => {
    if (!isZoneOpen || !blockElement) {
      return;
    }

    blockElement.dataset.fileImport = 'open';
    return () => {
      delete blockElement.dataset.fileImport;
    };
  }, [isZoneOpen, blockElement]);

  useEffect(() => {
    if (!blockElement) {
      return;
    }

    const handleDragEnter = (event: DragEvent) => {
      if (Array.from(event.dataTransfer?.types ?? []).includes('Files')) {
        setIsZoneOpen(true);
      }
    };

    blockElement.addEventListener('dragenter', handleDragEnter);
    return () => blockElement.removeEventListener('dragenter', handleDragEnter);
  }, [blockElement]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      handleFiles(event.target.files);
    }

    event.target.value = '';
  };

  return (
    <>
      <input
        ref={inputRef}
        accept={ACCEPTED_FILE_EXTENSIONS.join(',')}
        className="hidden"
        data-testid="clause-file-import-input"
        onChange={handleInputChange}
        type="file"
      />
      <ImportFileToolbarButton
        isActive={isZoneOpen}
        isConverting={isConverting}
        onClick={() => (blockElement ? setIsZoneOpen((open) => !open) : inputRef.current?.click())}
      />
      {isZoneOpen &&
        blockElement &&
        createPortal(
          <FileDropZone
            isConverting={isConverting}
            onClose={() => setIsZoneOpen(false)}
            onFilesDropped={handleFiles}
            onUploadClick={() => inputRef.current?.click()}
          />,
          blockElement,
        )}
    </>
  );
};
